import { useState, useEffect, useCallback } from 'react';

const SCRIPT_ID = 'onlyoffice-api-script';

// Turn OFF OnlyOffice's "AutoFormat As You Type" for lists (typing "* " / "1. " no longer
// converts the line into a bullet/numbered list). OnlyOffice has no config/API toggle for
// this — it's a per-user setting persisted in localStorage on the editor's origin (same
// origin as the app in production). We seed the defaults ONCE so we don't stomp a user who
// later re-enables it via File → Advanced Settings → Proofing → AutoCorrect. Must run
// before the editor iframe initializes, hence it's called at the top of load().
const AUTOFORMAT_SEED_FLAG = 'pacs-autoformat-defaults-applied';
function seedAutoformatDefaults() {
  try {
    if (localStorage.getItem(AUTOFORMAT_SEED_FLAG)) return;
    localStorage.setItem('de-settings-autoformat-bulleted', '0');
    localStorage.setItem('de-settings-autoformat-numbered', '0');
    localStorage.setItem(AUTOFORMAT_SEED_FLAG, '1');
  } catch { /* localStorage unavailable — nothing to seed */ }
}

// OnlyOffice customization.zoom: -2 = "Fit to Width".
export const FIT_TO_WIDTH_ZOOM = -2;
/**
 * Create a DocsAPI.DocEditor that fills its container and opens at Fit to Width.
 *
 * The backend already puts zoom into the JWT-signed config (the document server
 * trusts the token over anything added client-side), so the zoom set here only
 * matters when JWT is off. The resize after onDocumentReady is what actually makes
 * Fit to Width stick: the editor measures its iframe while it is still laying out,
 * so it has to be told to recompute once the frame has its final size.
 *
 * `events` are merged over config.events; onDocumentReady is wrapped, not replaced.
 */
export function createOnlyOfficeEditor(containerId, config, events = {}) {
  const { onDocumentReady } = events;
  return new window.DocsAPI.DocEditor(containerId, {
    ...config,
    editorConfig: {
      ...config.editorConfig,
      customization: {
        ...(config.editorConfig?.customization || {}),
        zoom: FIT_TO_WIDTH_ZOOM,
      },
    },
    width: '100%',
    height: '100%',
    events: {
      ...config.events,
      ...events,
      onDocumentReady: (...args) => {
        onDocumentReady?.(...args);
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      },
    },
  });
}

export default function useOnlyOfficeScript(onlyOfficeUrl) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!onlyOfficeUrl) return;

    // Disable list autoformat before the editor reads its settings
    seedAutoformatDefaults();

    // Already available on window
    if (window.DocsAPI) {
      setLoaded(true);
      return;
    }

    // Script tag already in DOM — wait for it
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true));
      existing.addEventListener('error', () => setError('Failed to load OnlyOffice script'));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${onlyOfficeUrl}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.onload = () => setLoaded(true);
    script.onerror = () => setError('Failed to load OnlyOffice script');
    document.head.appendChild(script);
  }, [onlyOfficeUrl]);

  useEffect(() => {
    setError(null);
    setLoaded(!!window.DocsAPI);
    load();
  }, [load]);

  return { loaded, error, retry: load };
}
